import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { PaginationService } from '../pagination.service';
import { BehaviorSubject, catchError, EMPTY, from, map, Observable, of, tap } from 'rxjs';
import { SsoSheetsService } from '../google-sheets/sso-sheets.service';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);
  private ssoSheetsService = inject(SsoSheetsService);
  private authService = inject(AuthService); // Inject the AuthService

  // The BehaviorSubject acts as a cache for our list of users.
  private _allUsersSubject = new BehaviorSubject<any[]>([]);
  public allUsers$ = this._allUsersSubject.asObservable(); // This is the public data stream.

  // The current user's email
  private _currentUserEmailSubject = new BehaviorSubject<string | null>(null);
  public currentUserEmail$ = this._currentUserEmailSubject.asObservable();
  
  constructor() {
    this.getCurrentUserEmail(); // Get the current user email on service initialization
  }

  /**
   * Retrieves a user by their ID from DynamoDB.
   * @param userId The ID of the user to retrieve.
   * @returns An observable of the user data with permissions.
   */
  public getUserById(userId: string): Observable<any> {
    const url = `${environment.URL_USERS_NT_GET_USER_BY_ID}?id=${encodeURIComponent(userId)}`;
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return this.http.get(url, options).pipe(
      map(response => {
        // If response is null, return null instead of causing downstream errors
        if (!response) {
          console.warn('API returned null - user may not exist in DynamoDB');
          return null;
        }
        return response;
      }),
      catchError(error => {
        console.error(`An error occurred while fetching user with ID ${userId}:`, error);
        return of(null); // Return null instead of EMPTY to prevent lastValueFrom errors
      })
    );
  }

  /**
   * Updates user metadata (firstName, lastName, phone numbers, etc.)
   * @param userId The user's email address
   * @param metadata Object containing metadata fields to save
   * @returns Observable of the API response
   */
  public updateUserMetadata(userId: string, metadata: any): Observable<any> {
    const url = environment.URL_USER_WRITE_METADATA;
    const body = {
      userId,
      metadata
    };
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    //console.log('UsersService.updateUserMetadata():', { userId, metadata });

    return this.http.put(url, body, options).pipe(
      tap(response => {
        //console.log('Metadata Update Response:', response);
      }),
      catchError(error => {
        console.error('UsersService | updateUserMetadata | Error:', error);
        throw error;
      })
    );
  }







  private _getPageOfUsers(lastEvaluatedKey: any = null): Observable<any> {
    const body = lastEvaluatedKey ? { lastEvaluatedKey } : {};

    return this.http.post<any>(environment.URL_USERS_NT_PAGE_OF_USERS, body).pipe(
      catchError(error => {
        console.error('user.service | _getPageOfUsers | Error fetching a page of users:', error);
        return EMPTY;
      })
    );
  }

  /**
   * Refreshes all users by calling the appropriate data source
   * and updating the BehaviorSubject.
   */
  public refreshAllUsersRecords(): void {
    // User-directory source is decoupled from the auth method. cbhi is SSO but its
    // user list (incl. activation state) lives in {org}-Users (DynamoDB), so it sets
    // `usersDirectorySource: 'backend'`. riverside/srh stay on the Google Sheet, and
    // any other org keeps the legacy behavior (sheet when SSO, else backend).
    const source = (environment as any).usersDirectorySource as 'backend' | 'sheet' | undefined;
    const useBackend =
      source === 'backend' ||
      (source !== 'sheet' &&
        !environment.sso &&
        environment.org !== 'riverside' &&
        environment.org !== 'srh');

    if (useBackend) {
      this.refreshAllUsersRecordsCognito();
    } else {
      this.refreshAllUsersRecordsSSO();
    }
  }

  private refreshAllUsersRecordsCognito(): void {
    this.paginationService.getAllPaginatedData(key => this._getPageOfUsers(key)).pipe(
      map(allUsers => {
        return allUsers.map((userData: any) => {
          // Support both new (firstName/lastName) and legacy (given_name/family_name) field names
          const firstName = (userData.firstName || userData.given_name || '').trim();
          const lastName = (userData.lastName || userData.family_name || '').trim();
          const email = userData.email || '';

          // Build display names - only show name format if we have at least one name part
          let shortFriendlyName: string;
          let friendlyName: string;

          if (lastName && firstName) {
            shortFriendlyName = `${lastName}, ${firstName}`;
            friendlyName = `${lastName}, ${firstName} (${email})`;
          } else if (lastName) {
            shortFriendlyName = lastName;
            friendlyName = `${lastName} (${email})`;
          } else if (firstName) {
            shortFriendlyName = firstName;
            friendlyName = `${firstName} (${email})`;
          } else {
            // No name available - just show email
            shortFriendlyName = email;
            friendlyName = email;
          }

          return {
            'email': email,
            'firstName': firstName,
            'lastName': lastName,
            // Keep legacy fields for backward compatibility
            'family_name': lastName,
            'given_name': firstName,
            'shortFriendlyName': shortFriendlyName,
            'friendlyName': friendlyName,
            // Carried through for the Manage Users activation table.
            'role': userData.role ?? null,
            'activated': userData.activated === true,
            // Automatic-email opt-outs for the Manage Users Emails column.
            // [] when absent = receives everything.
            'emailOptOuts': Array.isArray(userData.emailOptOuts) ? userData.emailOptOuts : [],
            // Permission scoping + login tracking, surfaced as Reports columns
            // (Ray). Written by the Permissions screen into userData.userData.*,
            // so resolve root-or-nested; kept as null/[] when absent rather than
            // dropped, so the columns render blank instead of undefined.
            // NOTE: the hc-users /users endpoint does not return these fields yet
            // (get-all-users-handler.js returns email/firstName/lastName/role/
            // activated only), so they stay empty until that ships.
            'legalEntities': userData.legalEntities ?? userData.userData?.userData?.legalEntities ?? [],
            'contractTypes': userData.contractTypes ?? userData.userData?.userData?.contractTypes ?? [],
            'databaseOrganizations': userData.databaseOrganizations ?? userData.userData?.userData?.databaseOrganizations ?? [],
            'departments': userData.departments ?? userData.userData?.userData?.departments ?? [],
            'sites': userData.sites ?? userData.userData?.userData?.sites ?? [],
            'lastLogin': userData.lastLogin ?? null
          };
        }).sort((a: any, b: any) => (a.lastName || a.email || '').localeCompare(b.lastName || b.email || ''));
      }),
      tap(transformedUsers => {
        this._allUsersSubject.next(transformedUsers);
      }),
      catchError(error => {
        console.error("Error fetching and refreshing all user records Cognito:", error);
        return EMPTY;
      })
    ).subscribe();
  }

  private refreshAllUsersRecordsSSO(): void {
    from(this.ssoSheetsService.ssoGetAllUsers()).pipe(
      map(usersRawArray => {
        let returnAllUsers: any[] = [];
        const org = environment.org;
        if (org === 'srh') {
          returnAllUsers = this.setupUserWhenEmailIsRowE(usersRawArray);
        } else {
          returnAllUsers = this.setupUserWhenEmailIsFirstRow(usersRawArray);
        }
        return returnAllUsers;
      }),
      tap(transformedUsers => this._allUsersSubject.next(transformedUsers)),
      catchError(error => {
        console.error("Error fetching and refreshing all SSO User records:", error);
        return EMPTY;
      })
    ).subscribe();
  }

  private setupUserWhenEmailIsRowE(usersRawArray: any): any[] {
    const returnAllUsers: any[] = [];
    if (!usersRawArray || !usersRawArray[0]) {
      console.warn('usersRawArray is invalid.');
      return [];
    }
    const lengthOfArray = usersRawArray[0].length;

    for (let i = 0; i < lengthOfArray; i++) {
      try {
        let userTitle = usersRawArray[3] && usersRawArray[3][i] ? usersRawArray[3][i] : '';
        let email = usersRawArray[4] && usersRawArray[4][i] ? usersRawArray[4][i] : null;
        let family_name = usersRawArray[2] && usersRawArray[2][i] ? usersRawArray[2][i] : null;
        let given_name = usersRawArray[1] && usersRawArray[1][i] ? usersRawArray[1][i] : null;

        if (email && family_name && given_name) {
          returnAllUsers.push({
            'email': email,
            'family_name': family_name,
            'given_name': given_name,
            'shortFriendlyName': `${family_name}, ${given_name} ${userTitle}`,
            'friendlyName': `${family_name}, ${given_name} ${userTitle} (${email})`,
            'title': userTitle,
            'userId': usersRawArray[0][i]
          });
        }
      } catch (error) {
        console.warn('Did NOT add this user: ', usersRawArray[0][i]);
        console.error(error);
      }
    }
    return returnAllUsers;
  }

  private setupUserWhenEmailIsFirstRow(usersRawArray: any): any[] {
    const returnAllUsers: any[] = [];
    if (!usersRawArray || !usersRawArray[0]) {
      console.warn('usersRawArray is invalid.');
      return [];
    }
    for (let i = 0; i < usersRawArray[0].length; i++) {
      let userTitle = usersRawArray[3] && usersRawArray[3][i] ? usersRawArray[3][i] : '';
      let email = usersRawArray[0] && usersRawArray[0][i] ? usersRawArray[0][i] : null;
      let given_name = usersRawArray[1] && usersRawArray[1][i] ? usersRawArray[1][i] : null;
      let family_name = usersRawArray[2] && usersRawArray[2][i] ? usersRawArray[2][i] : null;
      
      if (email && given_name && family_name) {
        returnAllUsers.push({
          'email': email,
          'family_name': family_name,
          'given_name': given_name,
          'shortFriendlyName': `${family_name}, ${given_name} ${userTitle}`,
          'friendlyName': `${family_name}, ${given_name} ${userTitle} (${email})`,
          'title': userTitle
        });
      }
    }
    return returnAllUsers;
  }


  // Add this function to your UsersService class
  public async getCurrentUserEmail() {
    const user = await this.authService.getCurrentUser();
    if (user) {
      user.getSession((err: any, session: any) => {
        if (err) {
          console.error('Error getting user session:', err);
          this._currentUserEmailSubject.next(null);
          return;
        }
        if (session) {
          const idToken = session.getIdToken().getJwtToken();
          const decodedToken = this.decodeJwt(idToken);
          this._currentUserEmailSubject.next(decodedToken.email);
        }
      });
    } else {
      this._currentUserEmailSubject.next(null);
    }
  }

  private decodeJwt(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  }

  /**
   * Gets a user's display name by email from the cached users list.
   * Returns "Last Name, First Name" format if available, otherwise returns the email.
   * @param email The user's email address
   * @returns The user's display name in "Last, First" format or the email if not found
   */
  public getUserDisplayNameByEmail(email: string | null | undefined): string {
    if (!email) return 'Not Assigned';

    // Get the current users from the cache
    const users = this._allUsersSubject.value;

    // Find the user by email
    const user = users.find(u => u.email === email);

    if (user && user.shortFriendlyName) {
      return user.shortFriendlyName; // Already in "Last, First" format
    }

    // Fallback to email if user not found
    return email;
  }

  /**
   * Gets all users from the cache as a Promise.
   * Useful for async/await patterns.
   * @returns Promise resolving to array of users
   */
  public async getAllUsersOnce(): Promise<any[]> {
    return this._allUsersSubject.value;
  }

  /**
   * Alias for refreshAllUsersRecords for convenience.
   */
  public refreshAllUsers(): void {
    this.refreshAllUsersRecords();
  }

  /**
   * Updates a single user in the local cache immediately.
   * This provides instant feedback after a profile update without waiting for a full refresh.
   * @param email The user's email address (used to find the user in cache)
   * @param firstName The updated first name
   * @param lastName The updated last name
   */
  public updateUserInCache(email: string, firstName: string, lastName: string): void {
    const users = this._allUsersSubject.value;
    const userIndex = users.findIndex(u => u.email?.toLowerCase() === email.toLowerCase());

    if (userIndex === -1) {
      // User not in cache yet - add them
      const newUser = this.buildUserObject(email, firstName, lastName);
      this._allUsersSubject.next([...users, newUser]);
    } else {
      // Update existing user
      const updatedUsers = [...users];
      updatedUsers[userIndex] = this.buildUserObject(email, firstName, lastName);
      this._allUsersSubject.next(updatedUsers);
    }
  }

  /**
   * Helper to build a user object with proper display names
   */
  private buildUserObject(email: string, firstName: string, lastName: string): any {
    const trimmedFirst = (firstName || '').trim();
    const trimmedLast = (lastName || '').trim();

    let shortFriendlyName: string;
    let friendlyName: string;

    if (trimmedLast && trimmedFirst) {
      shortFriendlyName = `${trimmedLast}, ${trimmedFirst}`;
      friendlyName = `${trimmedLast}, ${trimmedFirst} (${email})`;
    } else if (trimmedLast) {
      shortFriendlyName = trimmedLast;
      friendlyName = `${trimmedLast} (${email})`;
    } else if (trimmedFirst) {
      shortFriendlyName = trimmedFirst;
      friendlyName = `${trimmedFirst} (${email})`;
    } else {
      shortFriendlyName = email;
      friendlyName = email;
    }

    return {
      email: email,
      firstName: trimmedFirst,
      lastName: trimmedLast,
      family_name: trimmedLast,
      given_name: trimmedFirst,
      shortFriendlyName: shortFriendlyName,
      friendlyName: friendlyName
    };
  }

  /**
   * Updates a user's profile (firstName, lastName) in DynamoDB.
   * Uses PUT api/{org}/users/profile endpoint.
   * @param profileData Object containing email, firstName, lastName
   * @returns Promise that resolves when the update is complete
   */
  public async updateUserProfile(profileData: { email: string; firstName: string; lastName: string }): Promise<any> {
    const url = environment.URL_USERS_NT_USER_PROFILE_WRITE;
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    //console.log('UsersService | updateUserProfile:', profileData);

    try {
      const response = await this.http.put(url, profileData, options).toPromise();
      //console.log('UsersService | updateUserProfile | Success:', response);
      return response;
    } catch (error) {
      console.error('UsersService | updateUserProfile | Error:', error);
      throw error;
    }
  }

}
